import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  apiErrorCode,
  apiErrorStatus,
  getSessionGeneration,
  isDefinitiveAuthFailure,
  refreshAccessTokenSingleFlight,
  scheduleRefreshRetry,
} from '../lib/api';
import api from '../lib/api';

/**
 * Bootstrap silent refresh on app mount.
 *
 * Audit 2026-04-10 #6: the access token is no longer persisted to
 * localStorage (it's exfiltratable via XSS). Only the user profile is
 * persisted. On every page load, if a persisted user is present but no
 * in-memory access token, we exchange the httpOnly refresh cookie for a
 * fresh access token via /api/v1/auth/refresh.
 *
 * Concurrent API calls fired during this initial window are de-duped by
 * `refreshAccessTokenSingleFlight` in api.ts — they will all await the
 * same refresh promise rather than each triggering their own.
 *
 * Only a definitive rejection (400/401/403 with an AUTH_* / MISSING_TOKEN /
 * FAMILY_COMPROMISED code) clears the store and bounces the user to /login.
 * A network error, 429 or 5xx keeps the session and retries with backoff —
 * a clinic behind one NAT tripping the shared rate limiter, or an API
 * deploy, must not log everyone out.
 */
export const PROFILE_RETRY_BASE_MS = 1000;
export const PROFILE_RETRY_MAX_MS = 15_000;
export const PROFILE_RETRY_MAX_ATTEMPTS = 6;

export function useSilentRefresh(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const state = useAuthStore.getState();
    if (!state.hasSession) return; // not logged in — nothing to refresh
    if (state.accessToken) return; // already have a token in memory

    let cancelled = false;

    const loadProfile = async () => {
      const accessToken = useAuthStore.getState().accessToken;
      if (!accessToken) return;
      // A logout (or logout + a different user's login) while /users/me was
      // in flight must not get the old profile written back: setUser flips
      // hasSession to true and would restore the protected app.
      const generation = getSessionGeneration();
      const { data } = await api.get('/api/v1/users/me');
      if (generation !== getSessionGeneration()) return;
      useAuthStore.getState().setUser(data.data);
    };

    // The recovery callback is consumed the moment the refresh succeeds, so a
    // transient /users/me failure right after it would leave the store with a
    // token and no user until reload. Retry the profile itself, bounded, and
    // stop the moment the session ends. It is deliberately NOT routed through
    // the refresh-failure handler below: the session is healthy, so a profile
    // hiccup must neither show the "can't reach ClinikChat" banner nor rotate
    // the refresh cookie again.
    const hydrateProfileWithRetry = async () => {
      const generation = getSessionGeneration();
      for (let attempt = 0; attempt < PROFILE_RETRY_MAX_ATTEMPTS; attempt += 1) {
        try {
          await loadProfile();
          return;
        } catch (err) {
          if (generation !== getSessionGeneration()) return;
          // A valid session can still be blocked by the re-consent gate; keep
          // it alive so ReconsentModal can collect the acceptance.
          if (apiErrorCode(err) === 'CONSENT_REQUIRED') return;
          if (isDefinitiveAuthFailure(err)) {
            useAuthStore.getState().logout();
            navigate('/login', { replace: true });
            return;
          }
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              Math.min(PROFILE_RETRY_BASE_MS * 2 ** attempt, PROFILE_RETRY_MAX_MS),
            ),
          );
          if (generation !== getSessionGeneration()) return;
        }
      }
    };

    void refreshAccessTokenSingleFlight()
      .then(() => hydrateProfileWithRetry())
      .catch((err: unknown) => {
        if (cancelled) return;
        // A valid refreshed session can still be blocked by the API's
        // re-consent gate. Keep that session alive so ReconsentModal can
        // display and submit the required policy acceptance.
        if (apiErrorStatus(err) === 403 && apiErrorCode(err) === 'CONSENT_REQUIRED') {
          return;
        }
        if (isDefinitiveAuthFailure(err)) {
          useAuthStore.getState().logout();
          navigate('/login', { replace: true });
          return;
        }
        // Transient (offline, 429, 5xx, CORS/CSP block): keep the persisted
        // session, show the degraded banner and keep trying in the background.
        scheduleRefreshRetry(() => {
          void hydrateProfileWithRetry();
        });
      });

    return () => {
      cancelled = true;
    };
    // Run once on mount only — intentionally empty deps.
  }, []);
}
